// SEO constants + JSON-LD (schema.org) builders. Builders take the absolute
// site origin (baseUrl) so canonical/image URLs are absolute. Compose nodes into
// a single @graph via SeoHead.
import type { FaqItem } from "@/components/marketing/sections/faqData"

export const SITE = {
  name: "FullCast",
  legalName: "FullCast",
  description:
    "FullCast helps established founders, coaches, consultants, and expert-led businesses turn weekly video podcast interviews into authority-building content and strategic relationships.",
  ogImage: "/og-default.png",
  sameAs: ["https://www.linkedin.com/in/harryduran/"],
  founder: {
    name: "Harry Duran",
    jobTitle: "Founder",
    url: "https://www.linkedin.com/in/harryduran/",
    sameAs: ["https://www.linkedin.com/in/harryduran/"],
  },
  // Harry's own shows. Emitted as PodcastSeries nodes credited to him rather
  // than as sameAs, because sameAs means "another page about this same entity"
  // and a podcast is a different entity from the person who hosts it.
  podcasts: [
    {
      slug: "podcast-junkies",
      name: "Podcast Junkies",
      url: "https://podcastjunkies.com",
      startDate: "2014",
      description:
        "A conversation show with podcasters, founders and creatives about the craft and the people behind it.",
    },
    {
      slug: "vertical-farming-podcast",
      name: "Vertical Farming Podcast",
      url: "https://igrownews.com/podcast/vertical-farming",
      startDate: "2020",
      description:
        "A long-form interview show on controlled-environment agriculture, food security, and the capital reshaping how food gets grown.",
    },
  ],
} as const

const orgId = (base: string) => `${base}/#organization`
const siteId = (base: string) => `${base}/#website`
const personId = (base: string) => `${base}/#harry-duran`

export function organizationNode(base: string) {
  return {
    "@type": "Organization",
    "@id": orgId(base),
    name: SITE.name,
    legalName: SITE.legalName,
    url: `${base}/`,
    description: SITE.description,
    logo: `${base}/icon.png`,
    image: `${base}${SITE.ogImage}`,
    sameAs: SITE.sameAs,
    founder: { "@id": personId(base) },
  }
}

export function websiteNode(base: string) {
  return {
    "@type": "WebSite",
    "@id": siteId(base),
    name: SITE.name,
    url: `${base}/`,
    publisher: { "@id": orgId(base) },
  }
}

export function personHarryNode(base: string) {
  return {
    "@type": "Person",
    "@id": personId(base),
    name: SITE.founder.name,
    jobTitle: SITE.founder.jobTitle,
    url: SITE.founder.url,
    sameAs: SITE.founder.sameAs,
    worksFor: { "@id": orgId(base) },
    subjectOf: SITE.podcasts.map((p) => ({ "@id": `${base}/#${p.slug}` })),
  }
}

export function podcastSeriesNodes(base: string) {
  return SITE.podcasts.map((p) => ({
    "@type": "PodcastSeries",
    "@id": `${base}/#${p.slug}`,
    name: p.name,
    url: p.url,
    startDate: p.startDate,
    description: p.description,
    author: { "@id": personId(base) },
  }))
}

export function serviceNode(base: string) {
  return {
    "@type": "Service",
    "@id": `${base}/#partnership`,
    serviceType: "Podcast strategy and production",
    name: "The FullCast Podcast Growth Partnership",
    description:
      "A six-month podcast strategy, production, and relationship-building partnership for established experts who want their podcast to support authority, visibility, trust, and strategic relationships.",
    provider: { "@id": orgId(base) },
    areaServed: "Worldwide",
    audience: {
      "@type": "Audience",
      audienceType: "Founders, coaches, consultants, advisors, and expert-led businesses",
    },
  }
}

export function podcastAuditToolNode(base: string) {
  return {
    "@type": "WebApplication",
    "@id": `${base}/podcast-audit#tool`,
    name: "FullCast Podcast Discoverability Audit",
    url: `${base}/podcast-audit`,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Any (web browser)",
    description:
      "A free tool that grades a podcast's discoverability A to F from its RSS feed and Apple Podcasts search, using public data only.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    provider: { "@id": orgId(base) },
  }
}

export function faqPageNode(faqs: FaqItem[]) {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }
}

export function blogPostingNode(
  base: string,
  post: {
    title: string
    slug: string
    excerpt?: string | null
    author?: string | null
    published_at?: string | null
    updated_at?: string | null
    cover_image_url?: string | null
  },
) {
  return {
    "@type": "BlogPosting",
    "@id": `${base}/blog/${post.slug}#post`,
    headline: post.title,
    description: post.excerpt || SITE.description,
    datePublished: post.published_at || undefined,
    dateModified: post.updated_at || post.published_at || undefined,
    image: post.cover_image_url || `${base}${SITE.ogImage}`,
    author: { "@type": "Person", name: post.author || SITE.founder.name },
    publisher: { "@id": orgId(base) },
    mainEntityOfPage: `${base}/blog/${post.slug}`,
  }
}

export function breadcrumbNode(base: string, items: { name: string; path: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${base}${item.path}`,
    })),
  }
}
